import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Sparkles, CheckCircle2, ScanLine } from 'lucide-react';
import { createCustomerVehicle } from '../lib/customers';
import { CustomerVehicle } from '../types/customers';
import { decodeVin, fetchTrimOptions, isPlausibleVin, vinDecodeSummary } from '../lib/vinDecode';
import { useBusiness } from '../contexts/BusinessContext';
import VinScanModal from './VinScanModal';

interface CreateVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (vehicle: CustomerVehicle) => void;
  customerId: string;
}

export default function CreateVehicleModal({
  isOpen,
  onClose,
  onSuccess,
  customerId
}: CreateVehicleModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    trim: '',
    vin: '',
    color: '',
    paint_code: '',
    license_plate: '',
    mileage: 0
  });
  const { selectedBusiness } = useBusiness();
  const [decoding, setDecoding] = useState(false);
  const [decodeSummary, setDecodeSummary] = useState<string | null>(null);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const [vinScanOpen, setVinScanOpen] = useState(false);
  const [trimOptions, setTrimOptions] = useState<string[]>([]);
  // VIN that was last successfully decoded — while the field still holds it,
  // Decode/Scan are greyed out; clearing or changing the VIN re-enables them.
  const [decodedVin, setDecodedVin] = useState<string | null>(null);
  const vinLocked = !!decodedVin && formData.vin.trim().toUpperCase() === decodedVin;

  /** Load the trim lineup for the dropdown; empty result keeps free-text entry. */
  const loadTrimOptions = (year: number | null, make: string | null, model: string | null) => {
    if (!year || !make || !model) return;
    fetchTrimOptions(year, make, model)
      .then((trims) => {
        setTrimOptions(trims);
        if (trims.length === 0) return;
        // Snap a messy decoded trim (e.g. "XSE (Natl)") onto its real lineup entry.
        setFormData(prev => {
          const cur = prev.trim.trim();
          if (!cur || trims.includes(cur)) return prev;
          const lower = cur.toLowerCase();
          const match =
            trims.find(t => t.toLowerCase() === lower) ||
            [...trims]
              .sort((a, b) => b.length - a.length)
              .find(t => lower.startsWith(t.toLowerCase()));
          return match ? { ...prev, trim: match } : prev;
        });
      })
      .catch(() => {});
  };

  const runDecode = async (vin: string) => {
    setDecodeError(null);
    setDecodeSummary(null);
    if (!isPlausibleVin(vin)) {
      setDecodeError('Enter the full 17-character VIN first.');
      return;
    }
    setDecoding(true);
    try {
      const d = await decodeVin(vin);
      if (!d.valid) {
        setDecodeError(d.error || 'This VIN could not be decoded.');
        return;
      }
      setFormData(prev => ({
        ...prev,
        make: d.make || prev.make,
        model: d.model || prev.model,
        year: d.year || prev.year,
        trim: d.trim || prev.trim,
        color: d.exteriorColor || prev.color,
        paint_code: d.paintCode || prev.paint_code,
      }));
      setDecodeSummary(vinDecodeSummary(d));
      setDecodedVin(vin.trim().toUpperCase());
      loadTrimOptions(d.year, d.make, d.model);
    } catch (err) {
      console.error('VIN decode failed:', err);
      setDecodeError('Decode failed — check your connection and try again.');
    } finally {
      setDecoding(false);
    }
  };

  const handleDecodeVin = () => void runDecode(formData.vin);

  /** Photo scan captured a VIN — fill the field and decode immediately. */
  const handleVinScanned = (vin: string) => {
    setFormData(prev => ({ ...prev, vin }));
    void runDecode(vin);
  };

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Validate form whenever make or model changes
  React.useEffect(() => {
    setIsFormValid(formData.make.trim() !== '' && formData.model.trim() !== '');
  }, [formData.make, formData.model]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setFormData({
        make: '',
        model: '',
        year: new Date().getFullYear(),
        trim: '',
        vin: '',
        color: '',
        paint_code: '',
        license_plate: '',
        mileage: 0
      });
      setError(null);
      setDecodeSummary(null);
      setDecodeError(null);
      setDecodedVin(null);
      setTrimOptions([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      const newVehicle = await createCustomerVehicle({
        customer_id: customerId,
        ...formData,
        notes: '',
        is_primary: false,
        last_service: null
      });

      onSuccess(newVehicle);
    } catch (err) {
      console.error('Error creating vehicle:', err);
      setError(err instanceof Error ? err.message : 'Failed to create vehicle');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
      style={{
        zIndex: 100400,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100410
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Add Vehicle
            </h2>
          </div>

          {/* Form Content */}
          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`} style={{
              paddingBottom: isMobile ? 'calc(1rem + env(safe-area-inset-bottom, 0px))' : '1rem'
            }}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <span>{error}</span>
                  </div>
                </div>
              )}

              {/* Basic Information Section */}
              <div className="bg-white rounded-xl border border-gray-300 overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                  <h3 className="text-base font-semibold text-gray-900">Basic Information</h3>
                </div>
                <div className="p-4">
                
                  <div className="space-y-4">
                    {/* VIN first — decoding autofills everything below */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        VIN
                      </label>
                      {/* Full-width VIN input with the two actions attached below */}
                      <div className="rounded-xl border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all duration-150">
                        <input
                          type="text"
                          placeholder="17-character VIN"
                          value={formData.vin}
                          maxLength={17}
                          onChange={(e) => {
                            setFormData(prev => ({ ...prev, vin: e.target.value.toUpperCase() }));
                            setDecodeSummary(null);
                            setDecodeError(null);
                          }}
                          className="w-full px-4 py-3 border-0 focus:outline-none focus:ring-0 font-mono tracking-wider uppercase"
                          style={{ fontSize: '16px' }}
                        />
                        <div className="grid grid-cols-2 border-t border-gray-300 divide-x divide-gray-300">
                          <button
                            type="button"
                            onClick={handleDecodeVin}
                            disabled={decoding || vinLocked || !isPlausibleVin(formData.vin)}
                            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gray-50 text-gray-900 text-sm font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 active:bg-gray-200 touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            {decoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            Decode
                          </button>
                          <button
                            type="button"
                            onClick={() => setVinScanOpen(true)}
                            disabled={decoding || vinLocked}
                            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-gray-50 text-blue-600 text-sm font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 active:bg-gray-200 touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <ScanLine className="h-4 w-4" />
                            Scan
                          </button>
                        </div>
                      </div>
                      {decodeSummary && (
                        <p className="mt-2 flex items-start gap-1.5 text-sm text-emerald-700">
                          <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>{decodeSummary}</span>
                        </p>
                      )}
                      {decodeError && (
                        <p className="mt-2 text-sm text-red-600">{decodeError}</p>
                      )}
                      {!decodeSummary && !decodeError && vinLocked && (
                        <p className="mt-2 flex items-start gap-1.5 text-xs text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                          <span>VIN decoded — clear the field to enter or scan a different VIN.</span>
                        </p>
                      )}
                      {!decodeSummary && !decodeError && !vinLocked && (
                        <p className="mt-2 text-xs text-gray-400">
                          Enter the VIN and tap Decode to autofill year, make, model and trim.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Make *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Enter make"
                        value={formData.make}
                        onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Model *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Enter model"
                        value={formData.model}
                        onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Year *
                      </label>
                      <input
                        type="number"
                        min="1900"
                        max={new Date().getFullYear() + 1}
                        value={formData.year}
                        onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Trim
                      </label>
                      {trimOptions.length > 0 ? (
                        <select
                          value={formData.trim}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              // Switch back to free-text entry, keeping the current value.
                              setTrimOptions([]);
                              return;
                            }
                            setFormData(prev => ({ ...prev, trim: e.target.value }));
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 bg-white appearance-none"
                          style={{ fontSize: '16px' }}
                        >
                          <option value="">Select trim</option>
                          {/* Keep a decoded/typed trim selectable even if it's not in the lineup */}
                          {formData.trim && !trimOptions.includes(formData.trim) && (
                            <option value={formData.trim}>{formData.trim}</option>
                          )}
                          {trimOptions.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                          <option value="__custom__">Other (type manually)</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          placeholder="e.g. ZR2, Limited, Sport"
                          value={formData.trim}
                          onChange={(e) => setFormData(prev => ({ ...prev, trim: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information Section */}
              <div className="bg-white rounded-xl border border-gray-300 overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                  <h3 className="text-base font-semibold text-gray-900">Additional Information</h3>
                </div>
                <div className="p-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Color
                        </label>
                        <input
                          type="text"
                          placeholder="Enter color"
                          value={formData.color}
                          onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Paint Code
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. 1F7"
                          value={formData.paint_code}
                          onChange={(e) => setFormData(prev => ({ ...prev, paint_code: e.target.value.toUpperCase() }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 font-mono uppercase"
                          style={{ fontSize: '16px' }}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        License Plate
                      </label>
                      <input
                        type="text"
                        placeholder="Enter license plate"
                        value={formData.license_plate}
                        onChange={(e) => setFormData(prev => ({ ...prev, license_plate: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Mileage
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="Enter mileage"
                        value={formData.mileage}
                        onChange={(e) => setFormData(prev => ({ ...prev, mileage: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Submit Button */}
            <div className="bg-white p-6">
              <button
                type="submit"
                disabled={loading || !isFormValid}
                className={`w-full inline-flex items-center justify-center px-6 py-3.5 text-base font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                  loading || !isFormValid
                    ? 'bg-gray-300'
                    : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                    Creating Vehicle...
                  </>
                ) : (
                  'Create Vehicle'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      <VinScanModal
        isOpen={vinScanOpen}
        businessId={selectedBusiness?.id || ''}
        onClose={() => setVinScanOpen(false)}
        onScanned={handleVinScanned}
      />
    </>
  );
}